import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { inspectionDB } from '../data/database.structure';
import DatabaseInspectionsMethods from '../data/database.inspections.methods';
import { InspectionViewModel } from '../data/viewmodels.types';
import { queryKeys } from '../store/queryKeys';
import { EMutationKey } from '../store/mutationKeys';

export function useInspectionCached(externalId: string | undefined) {
  const queryClient = useQueryClient();

  const {
    data: inspection,
    error: inspectionError,
    isLoading: isLoadingInspection,
    refetch: refetchInspection,
    isFetching: isFetchingInspection,
  } = useQuery({
    queryKey: queryKeys.inspection(externalId || ''),
    queryFn: async () => {
      if (!externalId) {
        return null;
      }

      console.log('🔍 Fetching inspection from database:', externalId);
      const dbMethods = new DatabaseInspectionsMethods(inspectionDB);
      const response = await dbMethods.getInspectionByExternalId(externalId);

      if (!response) {
        throw new Error('Inspection not found');
      }

      console.log(
        '✅ Retrieved inspection:',
        externalId,
        'with',
        response.inspection.inspectionCategoryTypes.length,
        'categories',
      );
      return response;
    },
    enabled: !!externalId,
    // Increased stale time since cache invalidation is handled by mutations
    staleTime: 10 * 60 * 1000, // 10 minutes - only auto-refresh if very stale
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in memory longer
    // Removed automatic refetch interval since we use mutation-based invalidation
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    networkMode: 'always',
  });

  // Cache invalidation is handled by useObservationMutations hooks
  // No need for manual refetch listeners - this was causing double fetches

  return {
    inspection: inspection || null,
    inspectionError: inspectionError?.message || null,
    isLoadingInspection,
    isFetchingInspection,
    refetchInspection,
    // Indicate if data might be stale
    isStale:
      queryClient.getQueryState(queryKeys.inspection(externalId || ''))
        ?.isInvalidated || false,
  };
}
